import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { UUID, WorkflowChartGrain, WorkflowChartMeasure } from '@aline/common';
import {
  CustomOperatorValue,
  defaultWorkflowFileSort,
  encodeGroupedLeafRowId,
  encodeGroupRowPath,
  getIsWorkflowChartMeasure,
  parseGroupKeysFromGroupRowIds,
  WORKFLOW_CHART_MEASURES,
} from '@aline/common';
import type { ApolloLink } from '@apollo/client';
import { useApolloClient, useReactiveVar } from '@apollo/client/react';
import { GridLogicOperator } from '@mui/x-data-grid-premium';
import type {
  DataGridPremiumProps,
  GridApi,
  GridFilterItem,
  GridFilterModel,
  GridGetRowsParams,
  GridGetRowsResponse,
  GridRowId,
  GridRowModel,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import { isEqual, uniqBy } from 'lodash';
import { keys } from 'ramda';
import type { BoardLocalState, UserDefinedRoleIdToDetails, WorkflowListRow } from 'src/components';
import { useWorkflowReadOnlyContext } from 'src/components';
import { WorkflowInboxFilter, workflowInboxFilterVar } from 'src/components/dashboard';
import { getIsNotFound } from 'src/components/not-found/get-is-not-found';
import {
  getIsWorkflowListGroupRow,
  getIsWorkflowListWorkflowRow,
  getWorkflowIdFromListRow,
} from 'src/components/workflow/workflow-list/workflow-list-row-guards';
import { Apollo } from 'src/gql/generated';
import type { AlineApolloClient } from 'src/helpers';
import { workflowTypename } from 'src/helpers';
import { useExportDocumentsToPdf } from 'src/hooks';
import { Sentry } from 'src/sentry';
import type { ReactSetStateTuple } from 'src/types';
import { getIsPlainRecord } from 'src/utils/get-is-plain-record';
import { reportGroupRowIdDecodeError } from 'src/utils/group-row-id';
import { getWorkflowWasSentForSig } from 'src/workflow';
import { useDebouncedCallback } from 'use-debounce';

import { createWorkflowDataSourceCache } from './workflow-data-source-cache';

export function useGetWorkflowSimple(
  workflowId?: UUID | null,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetWorkflowSimpleQuery>[0]>, 'variables'>
): {
  isNotFound: boolean;
  loading: boolean;
  workflow: NonNullable<Apollo.GetWorkflowSimpleQueryHookResult['data']>['workflow'];
} {
  /**
   * Both reasons to skip, folded into one value and applied after the spread so
   * neither side can silently win. It also gates `isNotFound`: a skipped query
   * looks exactly like a miss — no data, no error.
   */
  const skip = !workflowId || Boolean(options?.skip);
  const { data, error, loading } = Apollo.useGetWorkflowSimpleQuery({
    variables: { workflowId: workflowId ?? '' },
    ...options,
    skip,
  });

  return {
    /**
     * The `workflow` resolver is nullable and returns null for both a missing
     * row and one the requester cannot see, so nothing is ever thrown.
     */
    isNotFound: !skip && getIsNotFound({ entity: data?.workflow, error, loading }),
    loading,
    workflow: data?.workflow ?? null,
  };
}

export function useTogglePublishTemplate(workflowId: UUID) {
  const [togglePublishTemplate, { loading }] = Apollo.useTogglePublishTemplateMutation({
    variables: { workflowId },
  });
  const safeTogglePublishTemplate = useCallback(
    () => togglePublishTemplate({ variables: { workflowId } }),
    [togglePublishTemplate, workflowId]
  );

  return {
    loading,
    togglePublishTemplate: safeTogglePublishTemplate,
  };
}

/**
 * Reverts a workflow to draft and refreshes any open activity feed so the "reverted to draft" row
 * appears instantly. Wrapping the mutation here (rather than notifying at each call site) keeps
 * every revert entry point — banner, info-box button, etc. — consistent.
 */
export function useRevertWorkflowToDraft() {
  const [revertToDraft, { loading }] = Apollo.useRevertWorkflowToDraftMutation();
  const safeRevertToDraft = useCallback(
    async (options: Parameters<typeof revertToDraft>[0]) => {
      return revertToDraft(options);
    },
    [revertToDraft]
  );

  return { loading, revertToDraft: safeRevertToDraft };
}

export function useGetWorkflowWithSteps(
  workflowId: UUID | null,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetWorkflowQuery>[0]>, 'variables'>
) {
  const { data, loading } = Apollo.useGetWorkflowQuery({
    ...options,
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
  });
  return { loading, workflow: data?.workflow ?? null };
}

export function useGetWorkflowForwardSettings(
  workflowId: UUID,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetWorkflowForwardSettingsQuery>[0]>,
    'variables'
  >
): {
  loading: boolean;
  workflow: NonNullable<Apollo.GetWorkflowForwardSettingsQueryHookResult['data']>['workflow'];
} {
  const { data, loading } = Apollo.useGetWorkflowForwardSettingsQuery({
    ...options,
    variables: { workflowId },
  });

  return {
    loading,
    workflow: data?.workflow ?? null,
  };
}

type WorkflowFilesHookData = NonNullable<Apollo.GetWorkflowFilesQueryHookResult['data']>;

export function useGetWorkflowFiles(
  workflowId?: UUID | null,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetWorkflowFilesQuery>[0]>, 'variables'>
): {
  loading: boolean;
  workflowFiles: NonNullable<WorkflowFilesHookData['workflow']>['files'];
  workflowWithFiles: WorkflowFilesHookData['workflow'];
} {
  const { data, loading } = Apollo.useGetWorkflowFilesQuery({
    ...options,
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
  });
  // Sorting helps with the presentation of files consistently in an Envelope UI and the Editor.
  const workflowFiles = data?.workflow?.files ?? [];
  const sortedWorkflowFiles = [...workflowFiles].sort(defaultWorkflowFileSort);

  return {
    loading,
    workflowFiles: sortedWorkflowFiles,
    workflowWithFiles: data?.workflow ?? null,
  };
}

type WorkflowFilesWithPartiesHookData = NonNullable<
  Apollo.GetWorkflowFilesWithPartiesQueryHookResult['data']
>;
export function useGetWorkflowFilesWithParties(
  workflowId?: UUID | null,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetWorkflowFilesWithPartiesQuery>[0]>,
    'variables'
  >
): {
  loading: boolean;
  workflowFiles: NonNullable<WorkflowFilesWithPartiesHookData['workflow']>['files'];
  workflowWithFiles: WorkflowFilesWithPartiesHookData['workflow'];
} {
  const { data, loading } = Apollo.useGetWorkflowFilesWithPartiesQuery({
    ...options,
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
  });

  return {
    loading,
    workflowFiles: data?.workflow?.files ?? [],
    workflowWithFiles: data?.workflow ?? null,
  };
}

export function useGetInboxWorkflows(
  options?: Omit<Parameters<typeof Apollo.useGetWorkflowInboxQuery>[0], 'variables'>
): {
  fetchMoreWorkflows: () => Promise<unknown>;
  hasMore: boolean;
  loading: boolean;
  totalCount: number;
  workflows: NonNullable<Apollo.GetWorkflowInboxQuery['workflowInboxWorkflows']['rows']>;
} {
  const filter = useReactiveVar(workflowInboxFilterVar);
  const { data, fetchMore, loading } = Apollo.useGetWorkflowInboxQuery({
    ...options,
    notifyOnNetworkStatusChange: true,
    variables: {
      cursor: null,
      filter: { assignedToMe: filter === WorkflowInboxFilter.AssignedToMe },
      limit: 25,
    },
  });
  const totalCount = data?.workflowInboxWorkflows?.meta?.totalCount ?? 0;
  const workflows = useMemo(
    () => data?.workflowInboxWorkflows?.rows ?? [],
    [data?.workflowInboxWorkflows?.rows]
  );
  const fetchMoreWorkflows = useCallback(async () => {
    const cursor = workflows.at(-1)?.id;
    if (!cursor || loading) return;
    return fetchMore({
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!prev.workflowInboxWorkflows?.rows || !fetchMoreResult?.workflowInboxWorkflows?.rows)
          return fetchMoreResult;
        // Find cursor position to handle proper slicing
        const cursorIndex = prev.workflowInboxWorkflows.rows.findLastIndex(workflow => {
          return workflow.id === cursor;
        });
        const cursorOffset = cursorIndex !== -1 ? cursorIndex + 1 : 0;
        return {
          __typename: 'Query',
          workflowInboxWorkflows: {
            ...fetchMoreResult.workflowInboxWorkflows,
            rows: uniqBy(
              [
                ...prev.workflowInboxWorkflows.rows.slice(0, cursorOffset),
                ...fetchMoreResult.workflowInboxWorkflows.rows,
              ],
              'id'
            ),
          },
        };
      },
      variables: {
        cursor,
      },
    });
  }, [workflows, loading, fetchMore]);
  return {
    fetchMoreWorkflows,
    hasMore: workflows.length < totalCount,
    loading,
    totalCount,
    workflows,
  };
}

/**
 * Hook that returns whether WorkflowSteps are editable.
 */
export function useAreWorkflowStepsEditable(workflowId: UUID): {
  areStepsEditable: boolean;
  loading: boolean;
  workflow: ReturnType<typeof useGetWorkflowSimple>['workflow'];
} {
  const { loading, workflow } = useGetWorkflowSimple(workflowId);
  const { getAreWorkflowStepsEditable } = useGetAreWorkflowStepsEditable();

  return {
    areStepsEditable: getAreWorkflowStepsEditable(workflow),
    loading,
    workflow,
  };
}

export function useGetAreWorkflowStepsEditable() {
  const { isReadOnly } = useWorkflowReadOnlyContext();
  const getAreWorkflowStepsEditable = useCallback(
    (workflow: Pick<Apollo.Workflow, 'state' | '__typename'> | null) => {
      const workflowSentForSig = getWorkflowWasSentForSig(workflow);
      return !workflowSentForSig && !isReadOnly;
    },
    [isReadOnly]
  );
  return { getAreWorkflowStepsEditable };
}

type WorkflowTasksHookData = NonNullable<Apollo.GetWorkflowTasksQueryHookResult['data']>;
type WorkflowPhase = NonNullable<NonNullable<WorkflowTasksHookData['workflow']>['phases']>[number];
type WorkflowNonSigningPhase = Extract<WorkflowPhase, { __typename: 'NonSigningPhase' }>;
type WorkflowPhaseEntry =
  | Omit<WorkflowNonSigningPhase, 'tasks'>
  | Exclude<WorkflowPhase, WorkflowNonSigningPhase>;
type WorkflowTask = WorkflowNonSigningPhase['tasks'][number];

export function useGetWorkflowTasks(
  workflowId: UUID | null,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetWorkflowTasksQuery>[0]>, 'variables'>
): {
  loading: boolean;
  phases: WorkflowPhaseEntry[];
  tasks: WorkflowTask[];
  workflow: WorkflowTasksHookData['workflow'];
} {
  const { data, loading } = Apollo.useGetWorkflowTasksQuery({
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
    ...options,
  });

  const allPhases = data?.workflow?.phases ?? [];
  const phases: WorkflowPhaseEntry[] = allPhases.map(p => {
    if (p.__typename !== 'NonSigningPhase') return p;
    const { tasks: _tasks, ...rest } = p;
    return rest;
  });
  const tasks = allPhases.flatMap(p => (p.__typename === 'NonSigningPhase' ? p.tasks : []));

  return { loading, phases, tasks, workflow: data?.workflow ?? null };
}

/**
 * Get the Workflow associated with a Document, if any.
 */
export function useGetDocumentWorkflow(documentId: UUID | null) {
  const { data, loading } = Apollo.useGetDocumentWithWorkflowQuery({
    skip: !documentId,
    variables: { documentId: documentId ?? '' },
  });
  return { loading, workflow: data?.document.workflow ?? null };
}

/**
 * Returns the "unused" actions in a Workflow (i.e., the actions that have no fields).
 */
export function useGetUnusedWorkflowActions(
  workflowId: UUID | null,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetUnusedWorkflowActionsQuery>[0]>,
    'variables'
  >
) {
  const { data, loading, refetch } = Apollo.useGetUnusedWorkflowActionsQuery({
    ...options,
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
  });

  return {
    loading,
    refetch,
    unusedActions: data?.workflow?.unusedActions ?? [],
  };
}

export function useMoveWorkflowStepActions(): {
  client: AlineApolloClient;
  moveWorkflowStepActions: Apollo.MoveWorkflowStepActionsMutationHookResult[0];
} {
  const [moveWorkflowStepActions, { client }] = Apollo.useMoveWorkflowStepActionsMutation();
  return { client, moveWorkflowStepActions };
}

export function useDeleteWorkflow(workflowId: UUID): {
  deleteWorkflow: () => Promise<ApolloLink.Result<Apollo.DeleteWorkflowMutation>>;
  loading: boolean;
} {
  const [deleteWorkflow, { loading }] = Apollo.useDeleteWorkflowMutation({
    variables: { workflowId },
  });

  return {
    deleteWorkflow: () =>
      // Variables provided at hook level
      deleteWorkflow({
        update: cache => {
          cache.modify({
            fields: (_, { DELETE }) => DELETE,
            id: cache.identify({ __typename: workflowTypename, id: workflowId }),
          });
        },
        variables: { workflowId },
      }),
    loading,
  };
}

/**
 * Download all the documents in a workflow.
 */
export function useExportWorkflowFiles(workflowId?: UUID | null) {
  const { workflowFiles } = useGetWorkflowFiles(workflowId ?? null);
  // Uniq documentIds since we're not sure which route we will be at (document route vs. workflow route in editor).
  const documentIdSet = new Set(workflowFiles.map(workflowFile => workflowFile.document.id));
  const { exportToPdf, loading } = useExportDocumentsToPdf([...documentIdSet]);

  return {
    exportToPdf,
    loading,
    workflowFiles,
  };
}

/**
 * A Workflow read back from the Apollo cache carries no guarantee that the list's
 * required fields were part of the query that wrote it, so check them at runtime.
 */
function getIsWorkflowListRow(workflow: unknown): workflow is WorkflowListRow {
  if (!getIsPlainRecord(workflow)) return false;

  return Boolean(
    workflow.id &&
    workflow.__typename === 'Workflow' &&
    Array.isArray(workflow.files) &&
    Array.isArray(workflow.labels) &&
    Array.isArray(workflow.permissions) &&
    Array.isArray(workflow.parties) &&
    workflow.owner
  );
}

export function useWorkflowDataSource(
  workflowFilter: Apollo.WorkflowFilterInput,
  options: {
    apiRef: React.RefObject<GridApi | null>;
    grain?: WorkflowChartGrain;
    initialFilterModel?: GridFilterModel;
    initialSortModel?: GridSortModel;
    rowGroupingModel?: string[];
  }
): {
  dataSource: DataGridPremiumProps['dataSource'];
  dataSourceCache: DataGridPremiumProps['dataSourceCache'];
  filterModelTuple: ReactSetStateTuple<GridFilterModel>;
  isGrouped: boolean;
  sortModelTuple: ReactSetStateTuple<GridSortModel>;
  totalCount: number;
  onDataSourceError?: DataGridPremiumProps['onDataSourceError'];
} {
  const client = useApolloClient();
  const { apiRef, grain, initialFilterModel, initialSortModel, rowGroupingModel = [] } = options;
  const [totalCount, setTotalCount] = useState<number>(0);
  const prevSortModelRef = useRef<GridSortModel | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel ?? []);
  const prevFilterModelRef = useRef<GridFilterModel | null>(null);
  const prevGrainRef = useRef<WorkflowChartGrain | undefined>(grain);
  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    initialFilterModel ?? {
      items: [],
      logicOperator: GridLogicOperator.And,
      quickFilterValues: [],
    }
  );
  const prevWorkflowFilterRef = useRef<Apollo.WorkflowFilterInput | null>(workflowFilter);
  const prevRowGroupingModelRef = useRef<string[]>(rowGroupingModel);
  const isGrouped = rowGroupingModel.length > 0;
  /**
   * Workflow filters are external to MUI's getRows params, so they cannot be
   * represented in the cache key. Give each cohort its own cache instead.
   */
  const dataSourceCache = useMemo(createWorkflowDataSourceCache, [grain, workflowFilter]);

  useEffect(() => {
    const observable = client.watchQuery<
      Apollo.GetWorkflowsQuery,
      Apollo.GetWorkflowsQueryVariables
    >({
      fetchPolicy: 'cache-only',
      query: Apollo.GetWorkflowsDocument,
      variables: {
        filterModel,
        grain,
        groupFields: isGrouped ? rowGroupingModel : undefined,
        sortModel,
        workflowFilter,
      },
    });
    const subscription = observable.subscribe({
      error: err => {
        console.error('Error watching workflow cache:', err);
        Sentry.captureException(err);
      },
      next: ({ data }) => {
        if (isGrouped) return;
        const updatedWorkflows = data?.workflows?.rows ?? [];
        const rowsToUpdate: WorkflowListRow[] = updatedWorkflows.flatMap<WorkflowListRow>(
          workflow => {
            if (!workflow?.id) return [];
            const existingRow = apiRef.current?.getRow(workflow.id);
            if (!existingRow) return [];

            if (!getIsWorkflowListRow(workflow)) {
              console.warn('Workflow missing required fields:', workflow.id);
              return [];
            }

            // Skip update if data is identical — prevents infinite fetch loop caused by Apollo cache merge creating new references on every write.
            if (isEqual(existingRow, workflow)) return [];

            return [workflow];
          }
        );
        if (rowsToUpdate.length) apiRef.current?.updateRows(rowsToUpdate);
      },
    });

    return () => subscription.unsubscribe();
  }, [apiRef, client, filterModel, grain, isGrouped, rowGroupingModel, sortModel, workflowFilter]);

  const safeGetRows = useCallback(
    async (params: GridGetRowsParams): Promise<GridGetRowsResponse> => {
      const { end, filterModel, groupFields, groupKeys, paginationModel, sortModel, start } =
        params;
      /**
       * The grid debounces its refetches, so the `getRows` it runs can be one captured
       * before the last grouping change, and its response — landing last — is the one
       * the grid keeps. Read the grouping off the params built for this request rather
       * than the render that created this callback, so every request asks for the rows
       * the grid is currently shaped for.
       */
      const rowGroupingModel = groupFields ?? [];
      const isGrouped = rowGroupingModel.length > 0;
      const startRowId = getWorkflowCursorBeforeRowIndex(apiRef.current, start);
      const safePageSize = paginationModel?.pageSize ?? 50;
      const sortChanged = !isEqual(prevSortModelRef.current, sortModel);
      const filterChanged = !isEqual(prevFilterModelRef.current, filterModel);
      const grainChanged = prevGrainRef.current !== grain;
      const workflowFilterChanged = !isEqual(prevWorkflowFilterRef.current, workflowFilter);
      const groupingChanged = !isEqual(prevRowGroupingModelRef.current, rowGroupingModel);
      const canReuseCursor =
        !sortChanged &&
        !filterChanged &&
        !grainChanged &&
        !workflowFilterChanged &&
        !groupingChanged;
      const safeCursor = canReuseCursor ? startRowId : null;
      const limit = typeof start === 'number' ? end - start + 1 : safePageSize;

      if (
        filterChanged ||
        sortChanged ||
        grainChanged ||
        workflowFilterChanged ||
        groupingChanged
      ) {
        apiRef.current?.dataSource?.cache?.clear();
      }

      prevSortModelRef.current = sortModel;
      prevFilterModelRef.current = filterModel;
      prevGrainRef.current = grain;
      prevWorkflowFilterRef.current = workflowFilter;
      prevRowGroupingModelRef.current = rowGroupingModel;

      const extractedGroupKeys = parseGroupKeysFromGroupRowIds(groupKeys, rowGroupingModel, {
        onDecodeError: reportGroupRowIdDecodeError,
      });
      const { data } = await client.query<
        Apollo.GetWorkflowsQuery,
        Apollo.GetWorkflowsQueryVariables
      >({
        fetchPolicy: 'network-only',
        query: Apollo.GetWorkflowsDocument,
        variables: {
          cursor: safeCursor,
          filterModel,
          grain,
          groupFields: isGrouped ? rowGroupingModel : undefined,
          groupKeys: isGrouped ? extractedGroupKeys : undefined,
          limit,
          sortModel,
          workflowFilter,
        },
      });

      const workflowCount = data?.workflows?.meta?.totalCount ?? 0;
      setTotalCount(workflowCount);

      if (isGrouped) {
        const headers = data?.workflows?.groupHeaders ?? [];
        const aggregates = data?.workflows?.aggregates;
        const aggregateRow = getAggregateRowFromWorkflowAggregates(aggregates);
        if (headers.length) {
          return {
            aggregateRow,
            rowCount: data?.workflows?.groupCount ?? headers.length,
            rows: headers.map(header => ({
              [WORKFLOW_CHART_MEASURES.medianDurationDays]: header.avgCycleTime,
              [WORKFLOW_CHART_MEASURES.count]: header.workflowCount,
              childRowCount: header.childRowCount,
              groupField: header.groupField,
              groupKey: header.groupKey,
              groupLabel: header.groupLabel,
              id: header.id,
              isGroupRow: true,
              name: header.groupLabel,
              parentGroupId: header.parentGroupId,
              workflowCount: header.workflowCount,
            })),
          };
        }

        const leafRows = data?.workflows?.rows ?? [];
        const parentPath = encodeGroupRowPath(rowGroupingModel, extractedGroupKeys);
        return {
          aggregateRow,
          rowCount: data?.workflows?.groupCount ?? leafRows.length,
          rows: leafRows.flatMap(row => {
            if (!row?.id) return [];
            return [
              {
                ...row,
                id: encodeGroupedLeafRowId(parentPath, row.id),
                workflowId: row.id,
              },
            ];
          }),
        };
      }

      const allCachedRows = data?.workflows?.rows ?? [];
      const cursorIndex = safeCursor
        ? allCachedRows.findIndex(workflow => workflow.id === safeCursor)
        : -1;
      const newRows =
        cursorIndex !== -1
          ? allCachedRows.slice(cursorIndex + 1, cursorIndex + 1 + limit)
          : allCachedRows;

      return {
        aggregateRow: getAggregateRowFromWorkflowAggregates(data?.workflows?.aggregates),
        /**
         * -1 is "unknown row count", which keeps the list on "infinite loading" for
         * now and clears any count a previous grouped fetch left behind.
         * See https://mui.com/x/react-data-grid/server-side-data/lazy-loading/.
         */
        rowCount: -1,
        rows: newRows,
      };
    },
    [apiRef, client, grain, workflowFilter]
  );

  const onDataSourceError = useCallback((error: Error) => {
    console.error(error);
    Sentry.captureException(error);
  }, []);
  const dataSource = useMemo(
    () => ({
      getAggregatedValue: getWorkflowAggregatedValue,
      getChildrenCount: getWorkflowGroupChildrenCount,
      getGroupKey: (row: GridRowModel) => {
        if (getIsWorkflowListGroupRow(row) || getIsWorkflowListWorkflowRow(row)) return row.id;
        if (!getIsPlainRecord(row)) return '';
        if (typeof row.id === 'string' || typeof row.id === 'number') return String(row.id);
        return '';
      },
      getRows: safeGetRows,
    }),
    [safeGetRows]
  );

  return {
    dataSource,
    dataSourceCache,
    filterModelTuple: [filterModel, setFilterModel],
    isGrouped,
    onDataSourceError,
    sortModelTuple: [sortModel, setSortModel],
    totalCount,
  };
}

type WorkflowRowLookup = {
  getRow: (id: GridRowId) => unknown;
  getRowIdFromRowIndex: (index: number) => GridRowId;
};

export function getWorkflowAggregatedValue(row: unknown, field: string): string {
  if (!getIsWorkflowListGroupRow(row) || !getIsWorkflowChartMeasure(field)) return '';
  const valueByMeasure = {
    [WORKFLOW_CHART_MEASURES.count]: row.count ?? row.workflowCount,
    [WORKFLOW_CHART_MEASURES.medianDurationDays]: row.medianDurationDays ?? row.avgCycleTime,
  } satisfies Record<WorkflowChartMeasure, number | null | undefined>;
  const value = valueByMeasure[field];
  return typeof value === 'number' ? String(value) : '';
}

export function getWorkflowGroupChildrenCount(row: unknown): number {
  if (!getIsWorkflowListGroupRow(row)) return 0;
  return row.childRowCount ?? row.workflowCount;
}

/**
 * The `cursor` variable is a workflow UUID, so only a leaf workflow row can seed it.
 * The row before `start` is often not one: while lazy loading it can be a skeleton
 * placeholder the grid generated, or a group header, and grouped leaf rows carry an id
 * prefixed with their group path. Anything else pages from the start of the range.
 */
export function getWorkflowCursorBeforeRowIndex(
  api: WorkflowRowLookup | null,
  start: GridGetRowsParams['start']
): string | null {
  if (typeof start !== 'number' || start < 1 || !api) return null;
  const rowId = api.getRowIdFromRowIndex(start - 1);
  const row = rowId == null ? null : api.getRow(rowId);
  if (!getIsWorkflowListWorkflowRow(row)) return null;
  return getWorkflowIdFromListRow(row);
}

function getAggregateRowFromWorkflowAggregates(
  aggregates: { count: number; avgCycleTime?: number | null } | null | undefined
): GridGetRowsResponse['aggregateRow'] {
  if (!aggregates) return undefined;
  return {
    [WORKFLOW_CHART_MEASURES.medianDurationDays]: aggregates.avgCycleTime ?? null,
    [WORKFLOW_CHART_MEASURES.count]: aggregates.count,
  };
}

export function useGetWorkflowsCount(
  options?: Parameters<typeof Apollo.useGetWorkflowsCountQuery>[0]
) {
  const { data, loading } = Apollo.useGetWorkflowsCountQuery(options);

  return {
    loading,
    workflows: data?.workflows?.rows ?? [],
  };
}

export function useGetPublishedTemplateWorkflows(
  options?: Parameters<typeof Apollo.useGetWorkflowsWithFilesQuery>[0]
): {
  fetchMoreWorkflows: (
    cursor: UUID | null
  ) => ReturnType<ReturnType<typeof Apollo.useGetWorkflowsWithFilesQuery>['fetchMore']>;
  loading: boolean;
  publishedTemplateWorkflows: NonNullable<Apollo.GetWorkflowsWithFilesQuery['workflows']['rows']>;
  totalCount: number;
} {
  const { data, fetchMore, loading } = Apollo.useGetWorkflowsWithFilesQuery({
    ...options,
    variables: {
      ...options?.variables,
      workflowFilter: { published: true, variant: Apollo.WorkflowVariant.Template },
    },
  });
  const fetchMoreWorkflows = useCallback(
    (cursor: UUID | null) => {
      return fetchMore({
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!prev.workflows?.rows || !fetchMoreResult?.workflows?.rows) return fetchMoreResult;

          // Find cursor position to handle proper slicing
          const cursorIndex = prev.workflows.rows.findLastIndex(workflow => {
            return workflow.id === cursor;
          });
          const cursorOffset = cursorIndex !== -1 ? cursorIndex + 1 : 0;

          return {
            __typename: 'Query',
            workflows: {
              ...fetchMoreResult.workflows,
              rows: uniqBy(
                [...prev.workflows.rows.slice(0, cursorOffset), ...fetchMoreResult.workflows.rows],
                'id'
              ),
            },
          };
        },
        variables: {
          cursor,
        },
      });
    },
    [fetchMore]
  );

  return {
    fetchMoreWorkflows,
    loading,
    publishedTemplateWorkflows: data?.workflows?.rows ?? [],
    totalCount: data?.workflows?.meta?.totalCount ?? 0,
  };
}

// Templates hold a column so their phases have a home, but they are definitions, not board cards.
const BOARD_WORKFLOW_FILTER = { variant: Apollo.WorkflowVariant.Contract };

/**
 * Hook for fetching workflows for a specific board column (state) with pagination support.
 * Used by the board view to load workflows per column and support infinite scrolling.
 */
export function useGetBoardColumnWorkflows(args: {
  boardState: BoardLocalState;
  category: Apollo.ColumnCategory;
  /** Specific column ID to filter by (takes precedence over category) */
  columnId: UUID;
  apolloOpts?: Omit<Parameters<typeof Apollo.useGetBoardWorkflowsQuery>[0], 'variables'>;
  /** Specific board ID to filter by (takes precedence over category) */
  boardId?: UUID;
}): {
  fetchMoreWorkflows: () => Promise<unknown>;
  hasMore: boolean;
  loading: boolean;
  totalCount: number;
  workflows: NonNullable<Apollo.GetBoardWorkflowsQuery['workflows']['rows']>;
} {
  const { apolloOpts, boardId, boardState, category, columnId } = args;
  const [localSearchString, setLocalSearchString] = useState<string>(boardState.searchString);
  const debouncedSetLocalSearchString = useDebouncedCallback(
    (searchString: string) => setLocalSearchString(searchString),
    500
  );

  useEffect(() => {
    if (boardState.searchString !== localSearchString)
      debouncedSetLocalSearchString(boardState.searchString);
  }, [boardState.searchString, debouncedSetLocalSearchString, localSearchString]);

  const filterItems = useMemo(() => {
    const items: GridFilterItem[] = [];

    items.push({
      field: 'column',
      operator: CustomOperatorValue.WorkflowColumn,
      value: [{ __typename: 'Column' as const, id: columnId }],
    });

    items.push({
      field: 'column-category',
      operator: 'isAnyOf',
      value: [category],
    });

    if (boardId) {
      items.push({
        field: 'board',
        operator: CustomOperatorValue.WorkflowBoard,
        value: [{ __typename: 'Board', id: boardId }],
      });
    }

    if (boardState.filterLabels.length) {
      items.push({
        field: 'labels',
        operator: CustomOperatorValue.Label,
        value: boardState.filterLabels,
      });
    }

    if (boardState.filterAssignees.length) {
      items.push({
        field: 'permissions',
        operator: CustomOperatorValue.WorkflowAssigneeAnyOf,
        value: boardState.filterAssignees,
      });
    }

    if (boardState.filterPriorities?.length) {
      items.push({
        field: 'priority',
        operator: CustomOperatorValue.WorkflowPriority,
        value: boardState.filterPriorities,
      });
    }

    if (boardState.filterStates?.length) {
      items.push({
        field: 'state',
        operator: CustomOperatorValue.WorkflowState,
        value: boardState.filterStates,
      });
    }

    return items;
  }, [
    boardId,
    category,
    columnId,
    boardState.filterLabels,
    boardState.filterAssignees,
    boardState.filterPriorities,
    boardState.filterStates,
  ]);

  const { data, fetchMore, loading } = Apollo.useGetBoardWorkflowsQuery({
    ...apolloOpts,
    variables: {
      cursor: null,
      filterModel: {
        items: filterItems,
        linkOperator: 'and',
        quickFilterValues: localSearchString ? [localSearchString] : undefined,
      },
      limit: 25,
      workflowFilter: BOARD_WORKFLOW_FILTER,
    },
  });
  const totalCount = data?.workflows?.meta?.totalCount ?? 0;
  const workflows = useMemo(() => data?.workflows?.rows ?? [], [data?.workflows?.rows]);
  const fetchMoreWorkflows = useCallback(async () => {
    const cursor = workflows.at(-1)?.id;
    if (!cursor || loading) return;

    return fetchMore({
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!prev.workflows?.rows || !fetchMoreResult?.workflows?.rows) return fetchMoreResult;

        // Find cursor position to handle proper slicing
        const cursorIndex = prev.workflows.rows.findLastIndex(workflow => {
          return workflow.id === cursor;
        });
        const cursorOffset = cursorIndex !== -1 ? cursorIndex + 1 : 0;

        return {
          __typename: 'Query',
          workflows: {
            ...fetchMoreResult.workflows,
            rows: uniqBy(
              [...prev.workflows.rows.slice(0, cursorOffset), ...fetchMoreResult.workflows.rows],
              'id'
            ),
          },
        };
      },
      variables: {
        cursor,
      },
    });
  }, [workflows, loading, fetchMore]);

  return {
    fetchMoreWorkflows,
    hasMore: workflows.length < totalCount,
    loading,
    totalCount,
    workflows,
  };
}

export function useGetWorkflowInboxWorkflow(workflowId: UUID) {
  const { data, loading } = Apollo.useGetWorkflowInboxWorkflowQuery({
    variables: { workflowId },
  });
  return { loading, workflow: data?.workflow ?? null };
}

export function useGetMyMostUsedTemplateWorkflows(
  options?: Parameters<typeof Apollo.useGetMyMostUsedTemplateWorkflowsQuery>[0]
): {
  loading: boolean;
  templateWorkflows: NonNullable<
    Apollo.GetMyMostUsedTemplateWorkflowsQueryResult['data']
  >['myMostUsedTemplateWorkflows'];
} {
  const { data, loading } = Apollo.useGetMyMostUsedTemplateWorkflowsQuery(options);

  return {
    loading,
    templateWorkflows: data?.myMostUsedTemplateWorkflows ?? [],
  };
}

export type IntegrationRecordMap = Partial<Record<Apollo.Integration, string>>;
export interface CreateWorkflowInstanceArgs {
  integrationRecordMap: IntegrationRecordMap;
  moreDocumentIds: UUID[];
  parties: Pick<Apollo.Party, 'id' | 'name' | '__typename'>[];
  replacementFiles: { replacementFileId: UUID; templateId: UUID }[];
  roleAssignmentMap: UserDefinedRoleIdToDetails;
  // documentId -> name for contract.
  templateIdToContractName: Record<UUID, string>;
  templateWorkflowId: UUID;
  workflowName: string;
  alineFieldValues?: Apollo.AlineFieldValueInput[];
  excludedDocumentIds?: UUID[];
  intakeFormValues?: Record<string, unknown>;
}

export function useCreateWorkflowInstance() {
  const [createWorkflowInstance, { loading }] = Apollo.useCreateWorkflowInstanceMutation();

  return {
    createWorkflowInstance: async ({
      alineFieldValues,
      excludedDocumentIds = [],
      intakeFormValues,
      integrationRecordMap,
      moreDocumentIds,
      parties,
      replacementFiles,
      roleAssignmentMap,
      templateIdToContractName,
      templateWorkflowId,
      workflowName,
    }: CreateWorkflowInstanceArgs) => {
      const instanceAssignments: Apollo.InstanceAssignmentInput[] = Object.entries(
        roleAssignmentMap
      ).map(([roleId, assignRoleDetails]) => ({
        ...assignRoleDetails,
        userDefinedRoleId: roleId,
      }));
      const contractNameEntries = Object.entries(templateIdToContractName);

      return createWorkflowInstance({
        variables: {
          input: {
            alineFieldValues,
            excludedDocumentIds,
            instanceAssignments,
            intakeFormValues,
            integrationRecords: keys(integrationRecordMap).flatMap(integration => {
              const opportunityId = integrationRecordMap[integration];
              if (!opportunityId) return [];

              return [
                {
                  integration,
                  opportunityId,
                },
              ];
            }),
            moreDocumentIds,
            partyIds: parties.map(p => p.id),
            props: {
              documentNames: contractNameEntries.map(([templateId, instanceName]) => ({
                documentId: templateId,
                instanceName,
              })),
              name: workflowName,
            },
            replacementFiles,
            templateWorkflowId,
          },
        },
      });
    },
    loading,
  };
}

export function useGetWorkflowComments(
  workflowId: UUID,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetWorkflowCommentsQuery>[0]>, 'variables'>
) {
  const { data, loading } = Apollo.useGetWorkflowCommentsQuery({
    ...options,
    variables: { workflowId },
  });

  return {
    comments: data?.workflow?.comments ?? [],
    loading,
    workflow: data?.workflow ?? null,
  };
}

export function useDeclineSignatureReminders(workflowId: UUID | null, documentId: UUID | null) {
  const [mutate, { loading }] = Apollo.useDeclineSignatureRemindersMutation();

  const declineSignatureReminders = useCallback(async () => {
    if (!workflowId) return;
    await mutate({
      refetchQueries: documentId
        ? [{ query: Apollo.GetEditorDocumentDocument, variables: { documentId } }]
        : [],
      variables: { workflowId },
    });
  }, [documentId, mutate, workflowId]);

  return { declineSignatureReminders, loading };
}

/**
 * Apollo merges each event into the workflow cache, so open views re-render
 * without a refetch. The selection includes AI-build timestamps and files.
 * The document list and task carousel read files.
 */
export function useSubscribeToWorkflowsUpdated(): ReturnType<
  typeof Apollo.useOnWorkflowsUpdatedSubscription
> {
  return Apollo.useOnWorkflowsUpdatedSubscription({
    onError: error => {
      Sentry.captureException(error, { extra: { subscription: 'WorkflowsUpdated' } });
    },
  });
}

export function useStartWorkflowWork(workflowId: UUID): {
  loading: boolean;
  /** `columnId` picks where a phaseless workflow lands; a phased one follows its first phase. */
  startWorkflowWork: (columnId?: UUID) => Promise<void>;
} {
  const [startWorkflowWorkMutation, { loading }] = Apollo.useStartWorkflowWorkMutation();

  const startWorkflowWork = useCallback(
    async (columnId?: UUID) => {
      await startWorkflowWorkMutation({
        refetchQueries: [
          { query: Apollo.GetWorkflowPhasesDocument, variables: { workflowId } },
          { query: Apollo.GetWorkflowHeaderDocument, variables: { workflowId } },
        ],
        variables: { columnId, workflowId },
      });
    },
    [startWorkflowWorkMutation, workflowId]
  );

  return { loading, startWorkflowWork };
}
